import { useEffect, useRef } from 'react'
import { ConnectionState, DisconnectReason, getBrowser, Room, RoomEvent } from 'livekit-client'

import {
  clientLogError,
  clientLogInfo,
  clientLogWarn,
} from '@/src/lib/clientLog'

const TRANSPORTS_CREATED = 'transportsCreated'

type IceCandidateType = 'host' | 'srflx' | 'relay' | 'prflx'

type IceCandidateCounts = Record<IceCandidateType | 'other', number>

/**
 * Узкий срез PCTransport: нам нужны только ICE-колбэки, а не весь движок.
 *
 * Сам класс внутренний, поэтому цепляемся за поля, которые LiveKit выставляет
 * публично на транспорте, и не ходим в закрытые методы.
 */
type IceTransportLike = {
  onIceCandidate?: (candidate: RTCIceCandidate) => void
  onIceCandidateError?: (ev: Event) => void
  onIceConnectionStateChange?: (state: RTCIceConnectionState) => void
  getICEConnectionState?: () => RTCIceConnectionState
}

/**
 * Узкий срез RTCEngine: событие transportsCreated и уже созданные PC.
 */
type EngineLike = {
  on: (event: string, listener: (...args: unknown[]) => void) => void
  off: (event: string, listener: (...args: unknown[]) => void) => void
  pcManager?: {
    publisher?: IceTransportLike
    subscriber?: IceTransportLike
  }
}

export type UseConnectionTelemetryOptions = {
  room: Room
  roomName: string
  identity: string
  enabled?: boolean
  retryCount: number
}

/**
 * Собирает контекст, по которому в Loki можно найти конкретную попытку входа.
 */
function buildTelemetryContext(
  roomName: string,
  identity: string,
  retryCount: number,
): Record<string, unknown> {
  return {
    roomName,
    identity,
    browser: getBrowser(),
    retryCount,
  }
}

/**
 * Определяет тип ICE-кандидата: сначала поле `.type`, иначе разбор SDP-строки.
 *
 * Safari иногда не заполняет `.type`, и без разбора строки мы бы не увидели,
 * собрал ли телефон host/srflx/relay — а именно это пропало в инциденте.
 */
function readCandidateType(candidate: RTCIceCandidate): IceCandidateType | 'other' {
  const fromField = candidate.type

  if (
    fromField === 'host' ||
    fromField === 'srflx' ||
    fromField === 'relay' ||
    fromField === 'prflx'
  ) {
    return fromField
  }

  const match = candidate.candidate?.match(/\btyp\s+(\w+)/i)
  const parsed = match?.[1]?.toLowerCase()

  if (
    parsed === 'host' ||
    parsed === 'srflx' ||
    parsed === 'relay' ||
    parsed === 'prflx'
  ) {
    return parsed
  }

  return 'other'
}

/**
 * Достаёт RTCPeerConnection, если LiveKit оставил его на транспорте.
 *
 * Поле закрытое, поэтому читаем осторожно: нет объекта — просто не логируем
 * gathering state, а не падаем.
 */
function getPeerConnection(transport: IceTransportLike): RTCPeerConnection | undefined {
  const pc = (transport as IceTransportLike & { _pc?: RTCPeerConnection })._pc

  if (pc && typeof pc.addEventListener === 'function') {
    return pc
  }

  return undefined
}

/**
 * Цепляет ICE-наблюдателей, не затирая обработчики LiveKit.
 *
 * Кандидаты только считаем: в Loki уходит сводка host/srflx/relay, когда
 * gathering завершился или сменилось ICE-состояние. Построчный лог съедал
 * лимит API раньше, чем доезжала ошибка join.
 *
 * Если заменить колбэки, SDK перестанет слать локальные кандидаты на сигнал —
 * поэтому сначала зовём прежний обработчик, и только потом считаем.
 *
 * @param role - publisher или subscriber, чтобы в Loki отличить два peer connection
 */
function attachIceObservers(
  transport: IceTransportLike,
  role: 'publisher' | 'subscriber',
  getContext: () => Record<string, unknown>,
): () => void {
  const counts: IceCandidateCounts = {
    host: 0,
    srflx: 0,
    relay: 0,
    prflx: 0,
    other: 0,
  }

  const previousCandidate = transport.onIceCandidate
  const previousConnection = transport.onIceConnectionStateChange
  const previousError = transport.onIceCandidateError

  const onCandidate = (candidate: RTCIceCandidate) => {
    previousCandidate?.(candidate)

    const candidateType = readCandidateType(candidate)
    counts[candidateType] += 1
  }

  const onConnection = (state: RTCIceConnectionState) => {
    previousConnection?.(state)

    const payload = {
      ...getContext(),
      role,
      iceConnectionState: state,
      counts: { ...counts },
    }

    if (state === 'failed' || state === 'disconnected') {
      clientLogWarn(`ice connection ${role}: ${state}`, payload)
    } else {
      clientLogInfo(`ice connection ${role}: ${state}`, payload)
    }
  }

  const onError = (event: Event) => {
    previousError?.(event)

    const iceError = event as RTCPeerConnectionIceErrorEvent

    clientLogError(`ice candidate error ${role}`, {
      ...getContext(),
      role,
      errorCode: iceError.errorCode,
      errorText: iceError.errorText,
      url: iceError.url,
      counts: { ...counts },
    })
  }

  transport.onIceCandidate = onCandidate
  transport.onIceConnectionStateChange = onConnection
  transport.onIceCandidateError = onError

  const pc = getPeerConnection(transport)

  const onGathering = () => {
    if (!pc || pc.iceGatheringState !== 'complete') {
      return
    }

    clientLogInfo(`ice gathering ${role}: complete`, {
      ...getContext(),
      role,
      iceGatheringState: pc.iceGatheringState,
      iceConnectionState: pc.iceConnectionState,
      counts: { ...counts },
    })
  }

  if (pc) {
    pc.addEventListener('icegatheringstatechange', onGathering)
  }

  return () => {
    if (transport.onIceCandidate === onCandidate) {
      transport.onIceCandidate = previousCandidate
    }

    if (transport.onIceConnectionStateChange === onConnection) {
      transport.onIceConnectionStateChange = previousConnection
    }

    if (transport.onIceCandidateError === onError) {
      transport.onIceCandidateError = previousError
    }

    if (pc) {
      pc.removeEventListener('icegatheringstatechange', onGathering)
    }
  }
}

/**
 * Пишет в Loki, что браузер думает о подключении к комнате и об ICE.
 *
 * Серверные логи LiveKit не видят локальный gathering. Этот хук как раз
 * закрывает дыру, из‑за которой на iPhone Safari нельзя было понять,
 * собрал ли клиент кандидатов.
 *
 * @param enabled - если false, хук молчит; так его можно выключить на экране ошибки
 * @param retryCount - номер попытки из ActiveRoom, чтобы отличить ретраи в Loki
 */
export function useConnectionTelemetry({
  room,
  roomName,
  identity,
  enabled = true,
  retryCount,
}: UseConnectionTelemetryOptions) {
  const retryCountRef = useRef(retryCount)
  retryCountRef.current = retryCount

  useEffect(() => {
    if (!enabled || !room) {
      return
    }

    const getContext = () =>
      buildTelemetryContext(roomName, identity, retryCountRef.current)

    const handleConnectionStateChanged = (state: ConnectionState) => {
      clientLogInfo(`connection state: ${state}`, getContext())
    }

    const handleReconnecting = () => {
      clientLogWarn('room reconnecting', getContext())
    }

    const handleDisconnected = (reason?: DisconnectReason) => {
      clientLogWarn(`room disconnected: ${reason}`, {
        ...getContext(),
        reason,
      })
    }

    const handleConnected = () => {
      clientLogInfo('room connected', getContext())
    }

    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged)
    room.on(RoomEvent.Reconnecting, handleReconnecting)
    room.on(RoomEvent.Disconnected, handleDisconnected)
    room.on(RoomEvent.Connected, handleConnected)

    const iceCleanups: Array<() => void> = []
    const hookedTransports = new WeakSet<object>()

    /**
     * Вешает ICE-наблюдателей на транспорт один раз, чтобы не задвоить счётчики.
     */
    const hookTransport = (
      transport: IceTransportLike | undefined,
      role: 'publisher' | 'subscriber',
    ) => {
      if (!transport || hookedTransports.has(transport)) {
        return
      }

      hookedTransports.add(transport)
      iceCleanups.push(attachIceObservers(transport, role, getContext))
    }

    try {
      const engine = (room as Room & { engine?: EngineLike }).engine

      if (engine && typeof engine.on === 'function') {
        const onTransportsCreated = (...args: unknown[]) => {
          const publisher = args[0] as IceTransportLike | undefined
          const subscriber = args[1] as IceTransportLike | undefined

          clientLogInfo('ice transports created', getContext())
          hookTransport(publisher, 'publisher')
          hookTransport(subscriber, 'subscriber')
        }

        engine.on(TRANSPORTS_CREATED, onTransportsCreated)
        iceCleanups.push(() => {
          engine.off(TRANSPORTS_CREATED, onTransportsCreated)
        })

        hookTransport(engine.pcManager?.publisher, 'publisher')
        hookTransport(engine.pcManager?.subscriber, 'subscriber')
      }
    } catch {
      // Нет engine или pcManager — события комнаты всё равно пишем
    }

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged)
      room.off(RoomEvent.Reconnecting, handleReconnecting)
      room.off(RoomEvent.Disconnected, handleDisconnected)
      room.off(RoomEvent.Connected, handleConnected)

      iceCleanups.forEach((cleanup) => {
        try {
          cleanup()
        } catch {
          // Снимаем наблюдателей как получится: транспорт уже могли закрыть
        }
      })
    }
  }, [room, roomName, identity, enabled])
}
